import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { LevelChip } from '../Common';
import { Code } from '@mui/icons-material';

const AlgorithmicProblemCard = ({ problem }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        // Navigate to AlgorithmicProblemDetail page with the problem ID
        if (problem?._id) {
            navigate(`/algorithmic-problem/${problem._id}`);
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy':
                return 'success';
            case 'medium':
                return 'warning';
            case 'hard':
                return 'error';
            default:
                return 'default';
        }
    };

    if (!problem) {
        return null;
    }

    return (
        <Card 
            sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 4,
                }
            }}
            onClick={handleClick}
        >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Icon placeholder */}
                <Box 
                    sx={{ 
                        height: '100px',
                        width: '100px',
                        margin: '0 auto 1rem auto',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5',
                        border: '2px dashed #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Code sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Box>

                <Typography 
                    gutterBottom 
                    variant="h6" 
                    component="div"
                    sx={{ 
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        lineHeight: 1.3,
                        marginBottom: 1,
                        textAlign: 'center'
                    }}
                >
                    {problem.title}
                </Typography>
                
                {/* Difficulty and Level */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {problem.difficulty && (
                        <Chip
                            label={problem.difficulty.toUpperCase()}
                            size="small"
                            color={getDifficultyColor(problem.difficulty)}
                            variant="filled"
                        />
                    )}
                    {problem.level && (
                        <LevelChip level={problem.level} variant="filled" size="small" />
                    )}
                </Box>
                
                {/* Tags */}
                <Box sx={{ marginTop: 'auto' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                        {problem.tags && problem.tags.slice(0, 3).map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                    fontSize: '0.7rem',
                                    height: '24px'
                                }}
                            />
                        ))}
                        {problem.tags && problem.tags.length > 3 && (
                            <Chip
                                label={`+${problem.tags.length - 3} more`}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                    fontSize: '0.7rem',
                                    height: '24px',
                                    color: 'text.secondary'
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

AlgorithmicProblemCard.propTypes = {
    problem: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        difficulty: PropTypes.oneOf(['easy', 'medium', 'hard']),
        problemUrl: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
        language: PropTypes.shape({
            name: PropTypes.string
        }),
        level: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string
        }),
        task: PropTypes.shape({
            _id: PropTypes.string
        })
    }).isRequired
};

export default AlgorithmicProblemCard;

